import type { ModelRef, Ref } from 'vue';
import type { Calendar } from './Calendar'
import type { Cell } from './Cell'

export interface ISelectOptions {
  allowBetweenDays?: boolean
  minTimeLength?: Ref<number> //minutes
  fixTimeLength?: Ref<number> //minutes
}

export function useSlotSelect(cueCalendar: Ref<Calendar>, selected: ModelRef<Cell | undefined>, {
  minTimeLength = ref(0),
  fixTimeLength = ref(0),
  allowBetweenDays = false,
}: ISelectOptions) {
  function isBetweenDays({ from, to }: Cell) {
    const isBetween = !isSameDay(from, to);
    const isAllow = allowBetweenDays ?? false
    return {
      isBetween,
      isAllow,
      isError: !isAllow && isBetween,
    }
  }

  function setMiddleware(newSelected: Cell | undefined, disableMinTimeLength = false) {
    if (!newSelected) return

    let { from, to } = newSelected
    from = new Date(from)
    to = new Date(to)

    if (fixTimeLength.value) {
      to = addMinutes(from, fixTimeLength.value)
    } else {
      const duration = differenceInMinutes(to, from);

      if (
        !disableMinTimeLength &&
        minTimeLength.value &&
        duration < minTimeLength.value
      ) {
        to = addMinutes(to, minTimeLength.value - duration)
        const isBetween = isBetweenDays({ from, to })
        if (isBetween.isError) {
          to = startOfDay(addDays(from, 1));
        }
      }
    }

    const subset = cueCalendar.value.getAvailableSubset({ from, to });

    const { disabled } = cueCalendar.value.isCellDisabled(subset)

    if (disabled) return

    return subset;
  }

  function set(newSelected: Cell | undefined, disableMinTimeLength?: boolean) {
    selected.value = setMiddleware(newSelected, disableMinTimeLength)

    // this.on?.set?.(selected.value)
    return selected.value
  }

  function pushMiddleware({ from, to }: Partial<Cell>) {
    if (!selected.value) return
    const newSelected = { ...selected.value }

    if (from && +new Date(from) < +newSelected.from) newSelected.from = from
    if (to && +new Date(to) > +newSelected.to) newSelected.to = to

    const isDisallow = from && to && (cueCalendar.value.isCellDisabled(newSelected).disabled);
    if (isDisallow) return { from, to }

    return newSelected
  }

  function push({ from, to }: Partial<Cell>) {
    if (!selected.value) return
    const newSelected = pushMiddleware({ from, to })

    const setResult = set(newSelected, true)

    if (!setResult && from && to) {
      set({ from, to }, true)
    }
  }

  function shift({ from, to }: Partial<Cell>) {
    if (!selected.value) return
    const newSelected = { ...selected.value }

    if (from && +new Date(from) > +newSelected.from) newSelected.from = from
    if (to && +new Date(to) < +newSelected.to) newSelected.to = to

    set(newSelected, true)
  }

  function check({ from, to }: Cell): boolean {
    if (!selected.value) return false
    return +selected.value.from <= +from && +to <= +selected.value.to
  }

  function getPosition({ from, to }: Cell): {
    isEqual: Record<'from' | 'to' | 'tail' | 'head', boolean>
    isIn: Record<'from' | 'fromInset' | 'to' | 'toInset', boolean>
    isDouble: boolean
    isInset: boolean
    isAnotherTime: boolean
    betweenDays: {
      isBetween: boolean
      isAllow: boolean
      isError: boolean
    }
  } {
    //@ts-expect-error лучше пока не трогать
    if (!selected.value) return

    const isEqual = {
      from: +from === +selected.value.from,
      to: +to === +selected.value.to,
      tail: +to === +selected.value.from,
      head: +from === +selected.value.to,
    }

    const isIn = {
      from: isBetween(selected.value.from, from, selected.value.to),
      fromInset: isBetweenStrictly(selected.value.from, from, selected.value.to),
      to: isBetween(selected.value.from, to, selected.value.to),
      toInset: isBetweenStrictly(selected.value.from, to, selected.value.to),
    }

    const isDouble = isEqual.from && isEqual.to

    const isInset = !isEqual.from && !isEqual.to && isIn.fromInset && isIn.toInset

    const betweenDays = isBetweenDays({
      from: new Date(Math.min(+selected.value.from, +from)),
      to: new Date(Math.max(+selected.value.to, +to)),
    })

    const isAnotherTime = Object.values({ ...isEqual, ...isIn }).every((v) => !v)

    return {
      isEqual,
      isIn,
      isDouble,
      isInset,
      isAnotherTime,
      betweenDays,
    }
  }

  function clear() {
    set(undefined)
  }

  function select(
    { from, to }: Cell,
    _options?: {
      /**
       * Если true, то мы всегда будем ставить новую дату
       */
      overload?: boolean
      /**
       * single: используем обычную логику добавления слотов
       * range: всегда стараемся ДОБАВИТЬ новые слоты к выбранным
       */
      mode?: 'single' | 'range'
    },
  ) {
    const options: typeof _options = {
      mode: 'single',
      overload: false,
      ..._options,
    }

    if (selected.value === undefined) {
      set({
        //Если пусто, то ставим "как есть"
        from,
        to,
      })
    } else {
      const { isEqual, isIn, isDouble, isInset, betweenDays, isAnotherTime } =
        getPosition({ from, to })
      if (isDouble) {
        //Если селекты равны, то сбрасываем значение
        clear()
      } else if (options.overload) {
        //Ставим всегда новое значение
        set({ from, to })
      } else if (fixTimeLength.value) {
        //Если у нас есть fixTimeLength.value, то мы должны остановить логику и просто сделать set на текущих данных
        if (isIn.from && !isIn.fromInset) {
          clear()
        } else {
          set({ from, to })
        }
      } else if (isInset) {
        //Если выбран слот между краями селекта, то ставим этот слот
        set({ from, to })
      } else if (isAnotherTime) {
        switch (options.mode) {
          case 'range': {
            if (betweenDays.isError) {
              set({ from, to })
            } else {
              push({ from, to })
            }
            break
          }
          default:
            set({ from, to })
        }
      } else {
        if (isEqual.tail || isEqual.head) {
          //Если выбран слот по краям, то добавляем
          if (betweenDays.isError) {
            set({ from, to })
          } else {
            push({ from, to })
          }
        } else {
          if (isEqual.from && isIn.to) {
            //Если выбран крайний слот - обрезаем
            shift({ from: to })
          } else if (isEqual.to && isIn.from) {
            //Если выбран крайний слот - обрезаем
            shift({ to: from })
          }
        }
      }
    }
  }

  return {
    clear: () => set(undefined),
    check,
    select,
    selected,
  };
}

export type SlotSelect = ReturnType<typeof useSlotSelect>;
